import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { RecoverySheet } from '@/components/RecoverySheet';
import { LockedFeatureSheet, useSubscriptionLock } from '@/components/LockedFeatureSheet';
import { DEMO_RECOVERY, demoTotalsFor } from '@/utils/demoRecovery';

const BLUE = '#0A84FF';
const PILLARS = [
  { key: 'hydrate', label: 'Hydrate', img: '/recovery-hydrate.jpg?v=3' },
  { key: 'refuel', label: 'Refuel', img: '/recovery-refuel.jpg?v=3' },
  { key: 'sleep', label: 'Sleep', img: '/recovery-sleep.jpg?v=3' },
];

export const isoLocal = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Monday-first calendar dates for the week the user is in right now. */
export const currentWeekDates = () => {
  const monday = new Date();
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  monday.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return isoLocal(d);
  });
};

const PILLAR_TITLES = { hydrate: 'Hydrate', refuel: 'Refuel', sleep: 'Sleep' };

/** Static taste of the logger, shown blurred behind the paywall. */
const LockedRecoveryPreview = ({ pillar, targets }) => {
  const sample = DEMO_RECOVERY.week[DEMO_RECOVERY.week.length - 1];
  const headline = pillar === 'hydrate'
    ? `${(sample.water_ml / 1000).toFixed(1)}L`
    : pillar === 'refuel' ? `${sample.calories} kcal` : `${sample.sleep_hours}h`;
  const goal = pillar === 'hydrate'
    ? `${((targets?.water_ml || 2600) / 1000).toFixed(1)}L`
    : pillar === 'refuel' ? `${targets?.calories || 2400} kcal` : `${targets?.sleep_hours || 8}h`;
  const actions = pillar === 'hydrate'
    ? ['Glass', 'Bottle', 'Large']
    : pillar === 'refuel' ? ['Photo of your meal', 'Type what you ate', 'Usual meals'] : ['−', '7h 30m', '+'];

  return (
    <div className="pb-8">
      <p className="mt-1 font-heading font-black text-3xl text-white">
        {headline}<span className="text-base text-white/35 font-normal"> of {goal}</span>
      </p>
      <p className="text-xs text-white/40">Tracked against your weight, goal and training</p>
      <div className="mt-4 grid grid-cols-3 gap-2">
        {actions.map((label) => (
          <span key={label} className="py-3 rounded-xl border border-white/10 bg-white/[0.04] text-center font-heading font-bold text-sm text-white truncate px-2">
            {label}
          </span>
        ))}
      </div>
      <div className="mt-4 h-20 flex items-end gap-1.5">
        {DEMO_RECOVERY.week.map((d) => (
          <span key={d.date} className="flex-1 rounded-t-md"
                style={{ height: `${Math.min(100, (d.water_ml / 3000) * 100)}%`, background: BLUE }} />
        ))}
      </div>
    </div>
  );
};

/** Shared state + sheet for the recovery tiles. Each card can show its own date. */
export const useRecoveryLog = () => {
  const { api } = useAuth();
  const { unlocked } = useSubscriptionLock();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null); // { pillar, date }
  const [locked, setLocked] = useState(null); // pillar shown behind the paywall

  const load = useCallback(async () => {
    try {
      const res = await api.get('/recovery/today', {
        headers: { 'X-TZ-Offset': String(new Date().getTimezoneOffset()) },
      });
      setData(res.data);
      // The home week card listens so it never shows stale numbers after a log.
      window.dispatchEvent(new CustomEvent('recovery:changed'));
    } catch { /* tiles still render, they just show no progress */ }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const today = data?.date || isoLocal(new Date());

  // No real logs yet → day cards show sample numbers (no "Demo" label). Once data has
  // loaded we know it's genuinely empty, so nothing flashes while loading.
  const hasRealLogs = !!data && (data.week || []).some(
    (d) => (d.meals || 0) > 0 || d.water_source === 'logged' || d.sleep_source === 'confirmed');
  const demo = !!data && !hasRealLogs;

  // week is the last 7 days ending today, so always match on the date field.
  const realTotalsFor = (date) => {
    if (!data) return null;
    if (!date || date === today) return data.totals || {};
    return (data.week || []).find((d) => d.date === date) || null;
  };

  const totalsFor = (date) => {
    if (!data) return null;
    if (demo) return demoTotalsFor(date || today, today);
    return realTotalsFor(date);
  };

  const open = (pillar, date) => {
    if (date && date > today) return; // no logging ahead of time
    if (!unlocked) { setLocked(pillar); return; }
    setActive({ pillar, date: date || today });
  };

  const progress = (key, date) => {
    const t = totalsFor(date); const g = data?.targets || {};
    if (!t) return 0;
    if (key === 'hydrate') return g.water_ml ? Math.min(1, (t.water_ml || 0) / g.water_ml) : 0;
    if (key === 'refuel') return g.protein_g ? Math.min(1, (t.protein_g || 0) / g.protein_g) : 0;
    return g.sleep_hours ? Math.min(1, (t.sleep_hours || 0) / g.sleep_hours) : 0;
  };

  const value = (key, date) => {
    const t = totalsFor(date);
    if (!t) return '—';
    if (key === 'hydrate') return `${((t.water_ml || 0) / 1000).toFixed(1)}L`;
    if (key === 'refuel') return `${t.meals || 0} meal${(t.meals || 0) === 1 ? '' : 's'}`;
    return t.sleep_hours ? `${Math.round(t.sleep_hours * 10) / 10}h` : '—';
  };

  // The sheet always logs against real data, never the sample numbers.
  const sheetData = active && data
    ? { ...data, date: active.date, totals: realTotalsFor(active.date) || {} }
    : data;

  const sheet = (
    <>
      <RecoverySheet pillar={active?.pillar} date={active?.date} today={today}
                     open={!!active} onOpenChange={(o) => !o && setActive(null)}
                     data={sheetData} onChanged={load} />
      <LockedFeatureSheet feature={locked ? `${PILLAR_TITLES[locked]} log` : 'Recovery log'}
                          open={!!locked} onOpenChange={(o) => !o && setLocked(null)}>
        <LockedRecoveryPreview pillar={locked || 'hydrate'} targets={data?.targets} />
      </LockedFeatureSheet>
    </>
  );

  return { data, today, progress, value, open, sheet, reload: load };
};

/** Full-size tiles for rest days. `date` scopes them to that day card. */
export const RecoveryTiles = ({ log, date, testId = 'rest-day-recovery-blocks' }) => {
  const future = date && date > log.today;
  return (
    <div className="grid grid-cols-3 gap-2" data-testid={testId}>
      {PILLARS.map(({ key, label, img }) => (
        <button key={key} onClick={() => log.open(key, date)} disabled={future}
          className="relative rounded-xl overflow-hidden aspect-square active:scale-[0.97] transition-transform disabled:opacity-50"
          data-testid={`rest-day-tile-${key}`}>
          <img src={img} alt={label} className="w-full h-full object-cover" loading="lazy" decoding="async" />
          <span className="absolute left-0 right-0 bottom-0 h-1 bg-black/40">
            <span className="block h-full" style={{ width: `${log.progress(key, date) * 100}%`, background: BLUE }} />
          </span>
          <span className="absolute top-1.5 right-1.5 text-[10px] font-heading font-bold text-white/90 px-1.5 py-0.5 rounded-md bg-black/55"
                data-testid={`rest-day-tile-${key}-value`}>
            {future ? '—' : log.value(key, date)}
          </span>
        </button>
      ))}
    </div>
  );
};

/** Training days use the same full tiles as rest days, with breathing room above and below. */
export const RecoveryChips = ({ log, date, testId = 'training-day-recovery-chips' }) => {
  const future = date && date > log.today;
  return (
    <div className="grid grid-cols-3 gap-2 mt-5 mb-5" data-testid={testId}>
      {PILLARS.map(({ key, label, img }) => (
        <button key={key} onClick={() => log.open(key, date)} disabled={future}
          className="relative rounded-xl overflow-hidden aspect-square active:scale-[0.97] transition-transform disabled:opacity-50"
          data-testid={`training-day-chip-${key}`}>
          <img src={img} alt={label} className="w-full h-full object-cover" loading="lazy" decoding="async" />
          <span className="absolute left-0 right-0 bottom-0 h-1 bg-black/40">
            <span className="block h-full" style={{ width: `${log.progress(key, date) * 100}%`, background: BLUE }} />
          </span>
          <span className="absolute top-1.5 right-1.5 text-[10px] font-heading font-bold text-white/90 px-1.5 py-0.5 rounded-md bg-black/55"
                data-testid={`training-day-chip-${key}-value`}>
            {future ? '—' : log.value(key, date)}
          </span>
        </button>
      ))}
    </div>
  );
};
